import { FontOption } from './types';

// Cache keys for localStorage
const FONT_CACHE_KEY = 'brainfish-google-fonts-cache';
const FONT_CACHE_TIMESTAMP_KEY = 'brainfish-google-fonts-cache-timestamp';
const FONT_CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cache for loaded font stylesheets to prevent duplicate loads
const loadedFonts = new Set<string>();

/**
 * Get cached fonts from localStorage
 */
export const getCachedFonts = (): FontOption[] | null => {
  try {
    const timestamp = localStorage.getItem(FONT_CACHE_TIMESTAMP_KEY);
    if (!timestamp) return null;

    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > FONT_CACHE_DURATION_MS) {
      localStorage.removeItem(FONT_CACHE_KEY);
      localStorage.removeItem(FONT_CACHE_TIMESTAMP_KEY);

      return null;
    }

    const cached = localStorage.getItem(FONT_CACHE_KEY);
    if (!cached) return null;

    return JSON.parse(cached) as FontOption[];
  } catch {
    return null;
  }
};

/**
 * Save fonts to localStorage cache
 */
export const setCachedFonts = (fonts: FontOption[]): void => {
  try {
    localStorage.setItem(FONT_CACHE_KEY, JSON.stringify(fonts));
    localStorage.setItem(FONT_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // localStorage might be full or unavailable, ignore
  }
};

/**
 * Load a Google Font stylesheet dynamically
 */
export const loadFont = (fontFamily: string): void => {
  if (loadedFonts.has(fontFamily)) return;

  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
  loadedFonts.add(fontFamily);
};

/**
 * Clear the font cache from localStorage
 * Useful for forcing a fresh fetch of fonts
 */
export const clearFontCache = (): void => {
  try {
    localStorage.removeItem(FONT_CACHE_KEY);
    localStorage.removeItem(FONT_CACHE_TIMESTAMP_KEY);
  } catch {
    // localStorage might be unavailable, ignore
  }
};
